import { XmlRules } from '../xml-rules-check/xmlRules.class';
import { XmlToolbox } from '../xmlToolbox.class';
import { Xml2htmlAttributeWorker } from './xml2htmlAttributeWorker.class';
import { XmlRuleElement } from '../xml-rules-check/xmlRuleElement.class';

export class Xml2html {
  private static DataTagName = 'data-tagname';
  public static NoClosingTagClassName = 'xmlTagNoClosing';
  public static FloatingTagClassName = 'floating';
  public static CommentTagClassName = 'comment';

  private attributeWorker: Xml2htmlAttributeWorker;

  constructor(public rules: XmlRules) {
    this.attributeWorker = new Xml2htmlAttributeWorker(rules);
  }

  static getTagNameFromNode(node: Node): string {
    const elem = node as Element;
    let name = '';
    if (elem) {
      name = elem.getAttribute(Xml2html.DataTagName);
    }
    if (name === undefined || name === null || name === '') {
      throw new Error(`node has no DataTagName attribute ${XmlToolbox.GetNodeDebugContext(node)}`);
    }
    return name;
  }

  static isFloatingElement(node: Node): boolean {
    const elem = node as Element;
    if (elem && elem.classList) {
      return elem.classList.contains(Xml2html.FloatingTagClassName);
    }
    return false;
  }

  static isNoClosingElement(node: Node): boolean {
    const elem = node as Element;
    if (elem && elem.classList) {
      return elem.classList.contains(Xml2html.NoClosingTagClassName);
    }
    return false;
  }

  public ToHtml(xml: string): string {
    if (xml === '' || xml === undefined || xml === null) {
      return '';
    }

    // alert(xml);

    // remove newline / carriage return
    xml = xml.replace(/\n/g, '');
    xml = xml.replace(/\r/g, '');
    // remove whitespace (tabs) before tags
    xml = xml.replace(/[\t]+\</g, '<');
    // remove whitespace between tags
    xml = xml.replace(/\>[\t]+\</g, '><');
    // remove whitespace after tags
    xml = xml.replace(/\>[\t]+$/g, '>');

    const domparser = new DOMParser();
    const domdoc = domparser.parseFromString(xml, 'text/xml');

    const targetDocument = new Document();

    if (domdoc.documentElement === null) {
      return '';
    }

    const body = this.GetHtmlElementFromXmlElement(domdoc.documentElement, targetDocument);
    if (body && body.nodeType === Node.ELEMENT_NODE) {
      const html = (body as Element).outerHTML;
      return html;
    } else {
      throw new Error(`root node ${XmlToolbox.GetNodeDebugContext(body)} is no element`);
    }
  }

  createNewElementHtml(ruleElem: XmlRuleElement): string {
    const elem = this.createNewElementByName(ruleElem.tagName);
    return elem.outerHTML;
  }

  public createNewElementByName(tagName: string): Element {
    const tempDoc = new Document();
    const newElement = tempDoc.createElement('div');
    // floating element or not?
    const rule = this.rules.ruleElements.get(tagName);
    if (rule && rule.floating === true) {
      newElement.classList.add(Xml2html.FloatingTagClassName);
    }
    // visualize closing tag or not?
    if (rule && rule.empty === true) {
      newElement.classList.add(Xml2html.NoClosingTagClassName);
    }
    newElement.setAttribute(Xml2html.DataTagName, tagName);
    return newElement;
  }

  private GetHtmlElementFromXmlElement(node: Node, targetDocument: Document): Node {
    switch (node.nodeType) {
      case Node.ELEMENT_NODE: // element node
        const asElement = node as Element;
        const nodeName = asElement.nodeName;

        // create element node
        let newElement = targetDocument.createElement('div');

        // floating element or not?
        const rule = this.rules.ruleElements.get(nodeName);
        if (rule && rule.floating) {
          newElement.classList.add(Xml2html.FloatingTagClassName);
        }
        // visualize closing tag or not?
        if (rule && rule.empty) {
          newElement.classList.add(Xml2html.NoClosingTagClassName);
        }
        newElement.setAttribute(Xml2html.DataTagName, nodeName);

        // append child elements recursive
        const children = asElement.childNodes;
        if (children.length === 0) {
          newElement.appendChild(targetDocument.createTextNode('')); // to prevent non closing tags
        } else {
          for (let i = 0; i < children.length; ++i) {
            const childAsHtml = this.GetHtmlElementFromXmlElement(children[i], targetDocument);
            if (childAsHtml !== undefined) {
              newElement.appendChild(childAsHtml);
            }
          }
        }

        // insert attributes
        this.attributeWorker.writeAllXmlAttributesToHtml(asElement, newElement);

        return newElement;

      case Node.TEXT_NODE: // text node
        const content = (node as Element).textContent;
        if (content.length > 0) {
          return targetDocument.createTextNode(content);
        } else {
          return undefined;
        }

      case Node.COMMENT_NODE:
        // create element node
        newElement = targetDocument.createElement('div');
        newElement.classList.add(Xml2html.CommentTagClassName);
        newElement.setAttribute(Xml2html.DataTagName, 'comment');
        newElement.textContent = (node as Element).textContent;
        return newElement;

      /* yet not implemented types */
      case Node.PROCESSING_INSTRUCTION_NODE:
        console.warn(`nodeType "PROCESSING_INSTRUCTION_NODE" ${XmlToolbox.GetNodeDebugContext(node)} not implemented yet`);
        return undefined;

      case Node.DOCUMENT_NODE:
        throw new Error(`nodeType "Node.DOCUMENT_NODE" ${XmlToolbox.GetNodeDebugContext(node)} not implemented yet`);
      case Node.DOCUMENT_TYPE_NODE:
        throw new Error(`nodeType "Node.DOCUMENT_NODE" ${XmlToolbox.GetNodeDebugContext(node)} not implemented yet`);
      case Node.DOCUMENT_FRAGMENT_NODE:
        throw new Error(`nodeType "Node.DOCUMENT_FRAGMENT_NODE" ${XmlToolbox.GetNodeDebugContext(node)} not implemented yet`);

      /* Depreached node types */
      case Node.ATTRIBUTE_NODE:
        throw new Error(`nodeType "Node.ATTRIBUTE_NODE" ${XmlToolbox.GetNodeDebugContext(node)} is depreached`);
      case Node.CDATA_SECTION_NODE:
        throw new Error(`nodeType "Node.CDATA_SECTION_NODE" ${XmlToolbox.GetNodeDebugContext(node)} is depreached`);
      case Node.ENTITY_REFERENCE_NODE:
        throw new Error(`nodeType "Node.ENTITY_REFERENCE_NODE" ${XmlToolbox.GetNodeDebugContext(node)} is depreached`);
      case Node.ENTITY_NODE:
        throw new Error(`nodeType "Node.ENTITY_NODE" ${XmlToolbox.GetNodeDebugContext(node)} is depreached`);
      case Node.NOTATION_NODE:
        throw new Error(`nodeType "Node.NOTATION_NODE" ${XmlToolbox.GetNodeDebugContext(node)} is depreached`);

      default:
        // tslint:disable-next-line:max-line-length
        throw new Error(`unknown xmlElement nodeType ${node.nodeType} in value: ${XmlToolbox.GetNodeDebugContext(node)}`);
    }
  }
}
